import React, { useState, useEffect, useRef } from 'react';
import { Play, Heart } from 'lucide-react';
import SongMenu from '../SongMenu';

const AllMusicPage = ({ playSong, likedSongs, toggleLike, user }) => {
  const [songs, setSongs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(1);
  const [randomSeed, setRandomSeed] = useState(null);
  const [loadedPages, setLoadedPages] = useState(new Set());
  const observerTarget = useRef(null);

  const SONGS_PER_PAGE = 30;

  // Generate a random seed on component mount for consistent randomization
  useEffect(() => {
    const seed = Math.random().toString(36).substring(7);
    setRandomSeed(seed);
  }, []);

  // Fetch songs
  const fetchSongs = async (pageNum, seed) => {
    if (loading || !seed || loadedPages.has(pageNum)) {
      console.log(`⏭️ Skipping page ${pageNum} - already loaded or loading`);
      return;
    }
    
    setLoading(true);
    setLoadedPages(prev => new Set([...prev, pageNum]));
    
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/songs/random?page=${pageNum}&limit=${SONGS_PER_PAGE}&seed=${seed}`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        console.log(`✅ Fetched page ${pageNum}:`, data.length, 'songs');
        
        if (data.length === 0) {
          console.log('🛑 No more songs, stopping pagination');
          setHasMore(false);
        } else if (data.length < SONGS_PER_PAGE) {
          console.log(`📊 Received ${data.length} songs (less than ${SONGS_PER_PAGE}), this is the last page`);
          setHasMore(false);
        }
        
        setSongs(prev => {
          const newTotal = prev.length + data.length;
          console.log(`📚 Total songs loaded: ${newTotal}`);
          return [...prev, ...data];
        });
      } else {
        console.error('Failed to fetch songs:', response.status);
        setHasMore(false);
      }
    } catch (error) {
      console.error('Error fetching songs:', error);
      setHasMore(false);
    } finally {
      setLoading(false);
    }
  };

  // Initial load
  useEffect(() => {
    if (randomSeed) {
      fetchSongs(1, randomSeed);
    }
  }, [randomSeed]);

  // Infinite scroll observer
  useEffect(() => {
    if (!hasMore || !randomSeed) return;
    
    const observer = new IntersectionObserver(
      entries => {
        if (entries[0].isIntersecting && hasMore && !loading) {
          const nextPage = page + 1;
          console.log(`🔄 Intersection detected, loading page ${nextPage}...`);
          setPage(nextPage);
          fetchSongs(nextPage, randomSeed);
        }
      },
      { threshold: 0.1, rootMargin: '200px' }
    );

    const currentTarget = observerTarget.current;
    if (currentTarget) {
      observer.observe(currentTarget);
    }

    return () => {
      if (currentTarget) {
        observer.unobserve(currentTarget);
      }
    };
  }, [hasMore, loading, randomSeed, page]);

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="page-content">
      <div className="page-header">
        <h1>All Music</h1>
        <p>Explore our entire collection</p>
      </div>

      <div className="songs-grid" style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
        gap: '20px',
        marginTop: '24px'
      }}>
        {songs.map((song) => (
          <div 
            key={song.id}
            className="song-card"
            style={{
              background: '#1a1a1a',
              borderRadius: '8px',
              padding: '16px',
              transition: 'background 0.2s',
              cursor: 'pointer',
              position: 'relative'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = '#252525';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = '#1a1a1a';
            }}
          >
            <div style={{ position: 'relative', marginBottom: '12px' }}>
              <img 
                src={song.albumArt} 
                alt={song.title}
                style={{
                  width: '100%',
                  aspectRatio: '1',
                  objectFit: 'cover',
                  borderRadius: '4px'
                }}
              />
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  playSong(song, songs);
                }}
                style={{
                  position: 'absolute',
                  bottom: '8px',
                  right: '8px',
                  width: '48px',
                  height: '48px',
                  borderRadius: '50%',
                  background: '#9D4EDD',
                  border: 'none',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  opacity: 1,
                  transition: 'transform 0.2s',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.4)'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'scale(1.1)';
                  e.currentTarget.style.background = '#8B3CC5';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'scale(1)';
                  e.currentTarget.style.background = '#9D4EDD';
                }}
              >
                <Play size={20} fill="white" />
              </button>
            </div>
            
            <div style={{ marginBottom: '8px' }}>
              <h3 style={{
                fontSize: '14px',
                fontWeight: '600',
                color: '#fff',
                marginBottom: '4px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}>
                {song.title}
              </h3>
              <p style={{
                fontSize: '12px',
                color: '#999',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}>
                {song.artist}
              </p>
            </div>

            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <span style={{ fontSize: '12px', color: '#666' }}>
                {formatDuration(song.duration)}
              </span>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  toggleLike(song.id);
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '4px'
                }}
              >
                <Heart 
                  size={16} 
                  fill={likedSongs.has(song.id) ? '#9D4EDD' : 'none'}
                  color={likedSongs.has(song.id) ? '#9D4EDD' : '#666'}
                />
              </button>
            </div>

            {user && (
              <div style={{ position: 'absolute', top: '8px', right: '8px' }}>
                <SongMenu song={song} />
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Loading indicator */}
      {loading && (
        <div style={{
          textAlign: 'center',
          padding: '40px',
          color: '#999'
        }}>
          Loading more songs...
        </div>
      )}

      {/* Intersection observer target */}
      {hasMore && <div ref={observerTarget} style={{ height: '100px', marginTop: '40px' }} />}

      {/* End message */}
      {!hasMore && songs.length > 0 && (
        <div style={{
          textAlign: 'center',
          padding: '40px',
          color: '#999'
        }}>
          You've reached the end of our collection!
        </div>
      )}

      {/* Empty state */}
      {!loading && songs.length === 0 && (
        <div style={{
          textAlign: 'center',
          padding: '60px 20px',
          color: '#999'
        }}>
          <h3>No songs available</h3>
          <p>Check back soon for new music!</p>
        </div>
      )}
    </div>
  );
};

export default AllMusicPage;
